<template>
  <draggable-panel
    :visible="visible"
    :title="title"
    :icon="icon"
    :width="950"
    :tabs="tabs"
    :enable-expand="true"
    :is-expanded="isExpanded"
    :expanded-width="1460"
    :active-tab="activeTab"
    :initial-position="{ top: 60 }"
    :z-index="zIndex"
    @close="handleClose"
    @tab-change="handleTabChange"
    @panel-focus="handlePanelFocus"
    @expand-toggle="handleExpandToggle"
  >
    <!-- 内容区域 -->
    <div v-loading="loading" class="panel-content">
      <!-- 搜索表单 -->
      <div class="search-form">
        <el-form 
          :inline="true" 
          :model="searchForm" 
          class="inline-search-form" 
          size="small"
          label-width="80px"
        >
          <el-form-item class="form-item-mmsi">
            <el-input
              v-model="searchForm.mmsiOrName"
              placeholder="请输入MMSI或船名"
              clearable
              size="small"
              style="width: 160px;"
            />
          </el-form-item>
          
          <el-form-item label="时间范围" class="form-item-time">
            <el-date-picker
              v-model="searchForm.timeRange"
              type="datetimerange"
              range-separator="至"
              start-placeholder="开始时间"
              end-placeholder="结束时间"
              value-format="yyyy-MM-dd HH:mm:ss"
              :default-time="['00:00:00', '23:59:59']"
              :clearable="false"
              size="small"
              style="width: 350px;"
            />
          </el-form-item>
          
          <el-form-item class="form-item-buttons">
            <el-button 
              type="primary" 
              size="small" 
              @click="handleSearch" 
              icon="el-icon-search"
            >
              查询
            </el-button>
            <el-button 
              size="small" 
              @click="handleReset"
              icon="el-icon-refresh"
            >
              重置
            </el-button>
          </el-form-item>
        </el-form>
      </div>

      <!-- 数据表格 -->
      <div class="table-section">
        <el-table
          :data="tableData"
          border
          stripe
          size="small"
          height="320"
          v-loading="loading"
        >
          <el-table-column prop="vesselMmsi" label="船舶MMSI" width="120" fixed="left" />
          <el-table-column prop="vesselName" label="船舶名称" width="140" show-overflow-tooltip />
          <el-table-column prop="eventTime" label="时间" width="160">
            <template slot-scope="scope">
              {{ formatDateTime(scope.row.eventTime) }}
            </template>
          </el-table-column>
          <el-table-column prop="bridgeName" label="桥梁名称" width="140" show-overflow-tooltip />
          <el-table-column prop="longitude" label="经度" width="120">
            <template slot-scope="scope">
              {{ formatCoordinate(scope.row.longitude, 'lng') }}
            </template>
          </el-table-column>
          <el-table-column prop="latitude" label="纬度" width="120">
            <template slot-scope="scope">
              {{ formatCoordinate(scope.row.latitude, 'lat') }}
            </template>
          </el-table-column>
          <el-table-column prop="speed" label="航速(节)" width="100">
            <template slot-scope="scope">
              {{ scope.row.speed ? scope.row.speed.toFixed(1) : '-' }}
            </template>
          </el-table-column>
          <el-table-column prop="heading" label="船首向(°)" width="100">
            <template slot-scope="scope">
              {{ formatHeading(scope.row.heading) }}
            </template>
          </el-table-column>
          <el-table-column prop="approachDirectionName" label="接近方向" width="120">
            <template slot-scope="scope">
              <el-tag size="mini" :type="getDirectionTagType(scope.row.approachDirection)">
                {{getDirectionText(scope.row.approachDirection) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="180" fixed="right">
            <template slot-scope="scope">
              <el-button 
                size="mini" 
                type="success"
                @click="showShipTrack(scope.row)"
                :disabled="!scope.row.vesselMmsi"
                title="查看轨迹"
              >
                轨迹
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 分页组件 -->
      <div class="pagination-section">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.pageNum"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pagination.pageSize"
              layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total"
          small
        />
      </div>
    </div>
  </draggable-panel>
</template>

<script>
import { getBridgeCrossingRecords } from '@/api/comprehensive-query'
import { formatDate } from '@/utils/dateUtils'
import { convertToDMS } from '@/utils/coordinateUtils'
import DraggablePanel from '@/components/common/DraggablePanel.vue'

export default {
  name: 'BridgeNavigationPanel',
  components: {
    DraggablePanel
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    bridgeId: {
      type: [Number, String],
      default: null
    },
    zIndex: {
      type: Number,
      default: 1000
    }
  },
  data() {
    return {
      title: '桥梁通航记录',
      icon: 'el-icon-s-data',
      tabs: [
        { key: 'records', label: '通航记录' }
      ],
      activeTab: 'records',
      isExpanded: false, // 展开/收起状态
      loading: false,
      searchForm: {
        mmsiOrName: '',
        eventType: '',
        status: null,
        timeRange: this.getDefaultTimeRange()
      },
      tableData: [],
      pagination: {
        total: 0,
        pageNum: 1,
        pageSize: 20
      },
      sortField: '',
      sortOrder: '',
      detailDialogVisible: false,
      currentRecord: null
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.init()
      }
    },
    bridgeId() {
      if (this.visible) {
        this.pagination.pageNum = 1
        this.loadData()
      }
    }
  },
  mounted() {
    // 将面板提升到 body，避免被页面内浮层层叠上下文遮挡
    if (this.$el && this.$el.parentNode !== document.body) {
      document.body.appendChild(this.$el)
    }
  },
  beforeDestroy() {
    // 组件销毁时清理挂载节点
    if (this.$el && this.$el.parentNode === document.body) {
      document.body.removeChild(this.$el)
    }
  },
  methods: {
    init() {
      this.resetForm()
      this.loadData()
    },

    resetForm() {
      this.searchForm = {
        mmsiOrName: '',
        eventType: '',
        status: null,
        timeRange: this.getDefaultTimeRange()
      }
    },

    getDefaultTimeRange() {
      const now = new Date()
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      
      // 格式化为 yyyy-MM-dd HH:mm:ss 格式
      const formatDateTime = (date) => {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        const seconds = String(date.getSeconds()).padStart(2, '0')
        
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
      }
      
      const startTime = formatDateTime(today)
      const endTime = formatDateTime(now)
      
      return [startTime, endTime]
    },

    async loadData() {
      this.loading = true
      try {
        const params = {
          pageNum: this.pagination.pageNum,
          pageSize: this.pagination.pageSize
        }

        // 设置默认时间范围（必需参数）
        const timeRange = this.searchForm.timeRange || this.getDefaultTimeRange()
        params.startTime = timeRange[0]
        params.endTime = timeRange[1]

        // 添加桥梁ID
        if (this.bridgeId) {
          params.bridgeId = Number(this.bridgeId)
        }

        // 添加搜索条件
        if (this.searchForm.mmsiOrName) {
          params.mmsiOrName = this.searchForm.mmsiOrName.trim()
        }
        if (this.searchForm.eventType) {
          params.eventType = this.searchForm.eventType
        }
        if (this.searchForm.status !== null && this.searchForm.status !== '') {
          params.status = this.searchForm.status
        }

        const response = await getBridgeCrossingRecords(params)
        
        if (response && response.code === 200 && response.data) {
          this.tableData = response.data.list || []
          this.pagination.total = response.data.total || 0
        } else {
          this.tableData = []
          this.pagination.total = 0
          this.$message.error(response?.message || '查询失败')
        }
      } catch (error) {
        console.error('查询桥梁通航记录失败:', error)
        this.$message.error('查询失败，请稍后重试')
        this.tableData = []
        this.pagination.total = 0
      } finally {
        this.loading = false
      }
    },

    handleSearch() {
      this.pagination.pageNum = 1
      this.loadData()
    },

    handleReset() {
      this.resetForm()
      this.pagination.pageNum = 1
      this.loadData()
    },

    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.pageNum = 1
      this.loadData()
    },

    handleCurrentChange(page) {
      this.pagination.pageNum = page
      this.loadData()
    },

    handleSortChange(sort) {
      this.sortField = sort.prop
      this.sortOrder = sort.order
      this.loadData()
    },

    handleViewDetail(row) {
      this.currentRecord = row
      this.detailDialogVisible = true
    },

    handleProcessRecord(row) {
      this.$confirm('确认处理该记录？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        // TODO: 实现记录处理逻辑
        this.$message.success('处理成功')
        this.loadData()
      }).catch(() => {
        // 用户取消
      })
    },

    handleClose() {
      this.$emit('update:visible', false)
    },

    handleTabChange(tabKey) {
      this.activeTab = tabKey
    },
    
    handlePanelFocus() {
      this.$emit('panel-focus')
    },
    
    /**
     * 处理展开/收起切换
     */
    handleExpandToggle(expanded) {
      this.isExpanded = expanded
    },

        /**
     * 显示船舶轨迹
     * @param {Object} record - 通航记录
     */
    async showShipTrack(record) {
      if (!record || !record.vesselMmsi) {
        this.$message.warning('该记录缺少船舶MMSI，无法查看轨迹');
        return;
      }
      
      try {
        this.$message.info('正在查询船舶轨迹数据...');
        
        // 计算时间范围：事件前后各1小时
        const recordTime = new Date(record.eventTime);
        const startTime = new Date(recordTime.getTime() - 60 * 60 * 1000); // 1小时前
        const endTime = new Date(recordTime.getTime() + 60 * 60 * 1000); // 1小时后
        
        console.log('桥梁通航记录：开始查询船舶轨迹', {
          mmsi: record.vesselMmsi,
          shipName: record.vesselName,
          recordTime: record.eventTime,
          startTime: this.formatDateTime(startTime),
          endTime: this.formatDateTime(endTime)
        });
        
        // 直接调用API获取轨迹数据
        const { searchVesselTrajectory } = await import('@/api/vessel-trajectory');
        const params = {
          keyword: record.vesselMmsi,
          startTime: this.formatDateTime(startTime),
          endTime: this.formatDateTime(endTime),
          pageNum: 1,
          pageSize: 1000 // 获取足够多的数据
        };
        
        const res = await searchVesselTrajectory(params);
        
        if (res.code === 200 && res.data && res.data.list) {
          console.log('轨迹数据查询成功，原始数据条数:', res.data.list.length);
          
          // 发送事件，包含MMSI和完整的响应数据
          this.$emit('show-ship-track-with-data', record.vesselMmsi, {
            trajectoryData: res.data,
            recordData: record,
            timeRange: {
              startTime: this.formatDateTime(startTime),
              endTime: this.formatDateTime(endTime)
            },
            useLatestRecords: true,
            maxRecords: 10
          });
        } else {
          this.$message.error('查询轨迹数据失败：' + (res.message || '未知错误'));
        }
      } catch (error) {
        console.error('查询船舶轨迹失败:', error);
        this.$message.error('查询船舶轨迹失败，请检查网络连接');
      }
    },

    // 格式化日期时间
    formatDateTime(datetime) {
      if (datetime instanceof Date) {
        const year = datetime.getFullYear();
        const month = String(datetime.getMonth() + 1).padStart(2, '0');
        const day = String(datetime.getDate()).padStart(2, '0');
        const hour = String(datetime.getHours()).padStart(2, '0');
        const minute = String(datetime.getMinutes()).padStart(2, '0');
        const second = String(datetime.getSeconds()).padStart(2, '0');
        
        return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
      }
      return datetime ? formatDate(datetime) : '-';
    },

    // 格式化坐标
    formatCoordinate(coord, type) {
      if (!coord) return '-'
      return convertToDMS(coord, type)
    },

    // 格式化航向
    formatHeading(heading) {
      if (heading === null || heading === undefined || heading >= 360) {
        return '-'
      }
      return heading + '°'
    },

    // 获取事件类型标签样式
    getEventTypeTagType(eventType) {
      const typeMap = {
        navigation: '',
        overspeed: 'warning',
        deviation: 'danger',
        collision: 'danger'
      }
      return typeMap[eventType] || 'info'
    },

    // 获取方向标签样式
    getDirectionTagType(direction) {
      return direction === 0 ? 'success' : 'primary'
    },

    // 获取状态标签样式
    getStatusTagType(status) {
      const statusMap = {
        0: 'info',
        1: 'success',
        2: 'warning'
      }
      return statusMap[status] || 'info'
    },

    // 获取严重程度标签样式
    getSeverityTagType(level) {
      const levelMap = {
        1: 'success',
        2: 'info',
        3: 'warning',
        4: 'danger',
        5: 'danger'
      }
      return levelMap[level] || 'info'
    },

    // 获取方向文本
    getDirectionText(direction) {
      const directionMap = {
        0: '下行',
        1: '上行'
      }
      return directionMap[direction] || '-'
    },

    // 获取状态文本
    getStatusText(status) {
      const statusMap = {
        0: '正常',
        1: '已处理',
        2: '已忽略'
      }
      return statusMap[status] || '未知'
    },

    // 获取严重程度文本
    getSeverityText(level) {
      const levelMap = {
        1: '轻微',
        2: '一般',
        3: '较重',
        4: '严重',
        5: '特别严重'
      }
      return levelMap[level] || '未知'
    }
  }
}
</script>

<style scoped>
/* 面板内容样式 */
.panel-content {
  padding: 0;
  max-height: 68vh;
}

/* 搜索表单样式 */
.search-form {
  margin: 12px 16px;
  padding: 12px 16px;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

/* 内联搜索表单样式 */
.inline-search-form {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.inline-search-form .el-form-item {
  margin-bottom: 0;
  margin-right: 0;
  display: flex;
  align-items: center;
  white-space: nowrap;
}

.inline-search-form .el-form-item__label {
  margin-right: 8px;
  padding-right: 0;
  color: #606266;
  font-weight: 500;
  font-size: 13px;
  line-height: 1;
}

.inline-search-form .el-form-item__content {
  margin-left: 0 !important;
  line-height: 1;
}

/* 表单项特定样式 */
.form-item-mmsi {
  min-width: auto;
}

.form-item-time {
  min-width: auto;
}

.form-item-buttons {
  margin-left: auto;
}

.form-item-buttons .el-button {
  margin-left: 8px;
}

.form-item-buttons .el-button:first-child {
  margin-left: 0;
}

/* 响应式适配 */
@media (max-width: 1200px) {
  .inline-search-form {
    flex-wrap: wrap;
    gap: 12px;
  }
  
  .form-item-buttons {
    margin-left: 0;
    width: 100%;
    margin-top: 8px;
  }
  
  .form-item-time .el-date-editor {
    width: 300px !important;
  }
}

/* 表格容器样式 */
.table-section {
  padding: 5px;
}

/* 表格自定义样式 */
.el-table {
  border-radius: 6px;
  overflow: hidden;
}

.el-table th {
  background-color: #f5f7fa !important;
  color: #606266;
  font-weight: 600;
}

.el-table tbody tr:hover > td {
  background-color: #f5f7fa !important;
}



/* 分页容器样式 */
.pagination-section {
  margin-top: 15px;
  text-align: right;
}

/* 详情对话框样式 */
.record-detail {
  padding: 16px 0;
}

.detail-item {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  padding: 8px 0;
}

.detail-label {
  color: #606266;
  font-weight: 500;
  min-width: 100px;
  margin-right: 12px;
}

.detail-value {
  color: #303133;
  flex: 1;
  word-break: break-all;
}

/* 按钮样式优化 */
.el-button--text {
  padding: 4px 8px;
}

/* 标签样式优化 */
.el-tag {
  border-radius: 4px;
}

/* 辅助类 */
.el-divider {
  margin: 10px 0;
}
</style>
